/**
 * /social-capital — the founding-philosophy page.
 *
 * Content structure (per spec):
 *   1. Hero — "Building Social Capital for Everyone"
 *   2. Intro paragraph
 *   3. ILLUSTRATION 1: social-capital-board.jpg
 *   4. What Is Social Capital / Every Person / Lifetime Portfolio (with list)
 *   5. Learning / Sharing Knowledge / Volunteering (with list) / Creativity
 *   6. ILLUSTRATION 2: family-tree-home-graphics.jpg
 *   7. Meaningful Connections / Recognition (with list) / Opportunities (with list)
 *   8. Legacy That Continues to Grow / Better Digital Society
 *   9. ILLUSTRATION 3: memorial-grahic.jpg
 *  10. Ecosystem inline recap + progression + CTAs
 *
 * Kept as one page (not a multi-route) so search engines and readers
 * get the full manifesto in a single scroll.
 */
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Heart, GraduationCap, HeartHandshake,
         Leaf, Palette, Briefcase, Cpu, Users, ShieldCheck, Globe2, Activity } from "lucide-react";
import SocialCapitalEcosystem from "@/components/SocialCapitalEcosystem";
import PublicImpactTicker from "@/components/PublicImpactTicker";

const ASSET = {
  hero:        "https://customer-assets-39nsmqrw.emergentagent.net/job_kindness-hub-35/artifacts/sxycmkex_social-capital-board.jpg",
  family:      "https://customer-assets-39nsmqrw.emergentagent.net/job_kindness-hub-35/artifacts/4woepx8u_family-tree-home-graphics.jpg",
  memorial:    "https://customer-assets-39nsmqrw.emergentagent.net/job_kindness-hub-35/artifacts/q2ygwu99_memorial-grahic.jpg",
  // Founder-approved SocialPulse ecosystem infographic — "Don't let your
  // life's contributions disappear into yesterday's feed." Anchors the
  // philosophical intro visually before the detailed sections begin.
  infographic: "https://customer-assets-jai6qajn.emergentagent.net/job_8926e311-9d9d-42c8-99ab-8293bca61d49/artifacts/gm8knp65_socialpulse-advantage.png",
};

const PORTFOLIO_ITEMS = [
  "Educational achievements", "Professional accomplishments", "Volunteer service",
  "Community leadership", "Research projects", "Scientific discoveries",
  "Environmental conservation", "Photography and creative work",
  "Articles and educational content", "Mentoring and coaching",
  "Cultural preservation", "Local history documentation",
  "Nature observations", "Humanitarian work", "Open-source software contributions",
  "Innovation and entrepreneurship", "Public speaking", "Sports and recreation",
  "Artistic performances", "Lifelong learning milestones",
];

const VOLUNTEER_ITEMS = [
  "Environmental restoration", "Tree planting", "Wildlife conservation",
  "Community clean-up events", "Food banks", "Disaster relief",
  "Blood donation campaigns", "Animal welfare", "Mentoring youth",
  "Teaching digital skills", "Supporting local charities",
  "Cultural heritage preservation", "Public health awareness",
  "Accessibility initiatives", "Community gardening",
  "Fundraising and nonprofit support",
];

const RECOGNITION_ITEMS = [
  "Publishing educational content", "Sharing verified knowledge",
  "Documenting biodiversity", "Preserving cultural heritage",
  "Supporting scientific research", "Creating tutorials",
  "Helping answer community questions", "Participating in volunteer projects",
  "Mentoring others", "Building open knowledge resources",
  "Fact-checking information", "Organizing community events",
  "Leading local initiatives", "Supporting nonprofit organizations",
  "Promoting respectful discussions",
];

const OPPORTUNITY_ITEMS = [
  "Schools and universities", "Scholarships", "Academic competitions",
  "Research programs", "Internships", "Apprenticeships",
  "Employment opportunities", "Professional certifications",
  "Speaking engagements", "Leadership programs", "Volunteer organizations",
  "Grants and fellowships", "Community awards", "Advisory boards",
  "Nonprofit leadership roles",
];

const SOURCES = [
  { icon: GraduationCap, color: "#0ea5e9", title: "🎓 Education & Learning",       items: ["School", "University", "Online Courses", "Certifications", "Research"] },
  { icon: Leaf,          color: "#16a34a", title: "🌿 Nature & Environment",       items: ["Wildlife", "Nature Pulse", "Conservation", "Tree Planting", "Citizen Science"] },
  { icon: HeartHandshake,color: "#e11d48", title: "❤️ Volunteer & Community",      items: ["Food Banks", "Charity", "Disaster Relief", "Mentoring", "Community Events"] },
  { icon: Briefcase,     color: "#7c3aed", title: "💼 Career & Growth",             items: ["Skills", "Projects", "Portfolio", "Leadership", "Entrepreneurship"] },
  { icon: Cpu,           color: "#0369a1", title: "💡 Innovation & Technology",     items: ["Coding", "Robotics", "AI", "Open Source", "Inventions"] },
  { icon: Palette,       color: "#d97706", title: "🎨 Arts & Creativity",           items: ["Photography", "Music", "Painting", "Writing", "Film"] },
  { icon: Activity,      color: "#059669", title: "🏃 Health & Sports",             items: ["Fitness", "Team Sports", "Hiking", "Wellness", "Outdoor Activities"] },
  { icon: Globe2,        color: "#ea580c", title: "🌎 Culture & Travel",            items: ["Languages", "Heritage", "Museums", "Local History", "Cultural Exchange"] },
  { icon: Users,         color: "#b45309", title: "👨‍👩‍👧‍👦 Family & Mentorship",   items: ["Parenting", "Teaching", "Coaching", "Knowledge Sharing", "Lifelong Learning"] },
  { icon: ShieldCheck,   color: "#0d9488", title: "🤝 Community & Reputation",      items: ["Trust", "Collaboration", "Verified Contributions", "Recognition", "Positive Impact"] },
];

export default function SocialCapitalPage() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="social-capital-page">
      {/* HERO */}
      <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
        THE SOCIAL PULSE PHILOSOPHY
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight"
          style={{ color: "#0f172a" }}>
        Building Social Capital <br />
        <span style={{ color: "#059669" }}>for Everyone</span>
      </h1>
      <div className="mt-2 text-lg opacity-80">A reputation built on positive contributions.</div>

      {/* Live impact ticker — first-time visitors immediately feel the
          scale of the network. Refetches every 60s. */}
      <PublicImpactTicker />

      {/* INTRO */}
      <div className="prose-content mt-8 space-y-4 text-base sm:text-lg leading-relaxed"
           style={{ color: "#1f2937" }} data-testid="sc-intro">
        <p><strong>Social Pulse is founded on a simple but powerful belief:</strong></p>
        <p className="pl-4 border-l-4 italic text-xl sm:text-2xl font-display"
           style={{ borderColor: "#10b981" }}>
          A person's greatest value is not measured by how many followers
          they have, but by the positive impact they make on the lives
          of others.
        </p>
        <p>
          Traditional social networks reward popularity. They count likes,
          views, and followers, often encouraging content designed for
          attention rather than lasting value.
        </p>
        <p><strong>Social Pulse takes a different approach.</strong></p>
      </div>

      {/* INFOGRAPHIC — "Don't let your life's contributions disappear into
          yesterday's feed."  Full-bleed, tap-to-zoom on mobile so the small
          type inside stays legible without forcing a fixed pixel size. */}
      <figure className="my-12" data-testid="sc-infographic">
        <a
          href={ASSET.infographic}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-2xl overflow-hidden border shadow-sm hover:shadow-md transition-shadow"
          style={{ borderColor: "var(--wofy-border, rgba(0,0,0,0.08))", background: "#F8FAFC" }}
          data-testid="sc-infographic-link"
        >
          <img
            src={ASSET.infographic}
            alt="Don't let your life's contributions disappear into yesterday's feed — Post for today. Preserve for tomorrow. Contribute for generations."
            className="w-full h-auto block"
            loading="lazy"
          />
        </a>
        <figcaption className="text-center text-xs opacity-70 mt-3 font-mono uppercase tracking-wider">
          Today's Post · Tomorrow's Legacy — Tap to view full size
        </figcaption>
      </figure>

      {/* ILLUSTRATION 1 — Social Capital board */}
      <figure className="my-12" data-testid="sc-illustration-hero">
        <img src={ASSET.hero}
             alt="Social Pulse Social Capital — People. Connections. Trust. Stronger Together."
             className="w-full rounded-2xl border shadow-sm"
             style={{ borderColor: "var(--wofy-border)" }} />
        <figcaption className="text-center text-xs opacity-60 mt-2 font-mono uppercase tracking-wider">
          People · Connections · Trust · Stronger Together
        </figcaption>
      </figure>

      <div className="prose-content space-y-4 text-base sm:text-lg leading-relaxed" style={{ color: "#1f2937" }}>
        <p>
          The concept isn't just about children or students — it becomes a
          core philosophy of Social Pulse for people of every age. Instead
          of measuring popularity, Social Pulse measures positive
          contributions to society creating joy. That creates a platform
          where everyone — from students, all men and women, to retirees —
          can build a meaningful reputation through knowledge, service,
          creativity, and community engagement.
        </p>
        <p>
          Our goal is to help every person build <strong>Social Capital</strong>
          {" "}— a trusted digital reputation earned through learning, sharing
          knowledge, helping others, volunteering, creating meaningful work,
          and making positive contributions to communities around the world.
        </p>
      </div>

      {/* WHAT IS SOCIAL CAPITAL */}
      <Section title="What Is Social Capital?" id="what-is">
        <p>
          On Social Pulse, Social Capital represents the trust, credibility,
          knowledge, and positive impact a person builds over time.
        </p>
        <p>
          It is not something that can be bought or artificially inflated.
          It is earned through genuine participation and consistent
          contributions that benefit others.
        </p>
        <p>
          Every meaningful action helps strengthen your reputation and
          demonstrates your commitment to your community and the wider world.
        </p>
      </Section>

      <Section title="Every Person Can Build Social Capital" id="every-person">
        <p><strong>Social Capital is for everyone.</strong></p>
        <p>
          Whether you are a student beginning your educational journey, a
          teacher inspiring young minds, a scientist conducting research,
          an artist sharing creativity, a healthcare professional serving
          patients, an entrepreneur building businesses, a retiree mentoring
          future generations, or simply someone who enjoys helping others,
          your experiences and contributions matter.
        </p>
        <p><em>Every stage of life offers opportunities to learn, contribute, and leave a positive legacy.</em></p>
      </Section>

      <Section title="Building a Lifetime Portfolio" id="portfolio">
        <p>
          Social Pulse creates a living portfolio that grows alongside you.
          Rather than documenting only major achievements, it captures the
          experiences that define your personal journey and demonstrates how
          your interests, skills, and contributions evolve over time.
        </p>
        <p>Your profile may include:</p>
        <TagList items={PORTFOLIO_ITEMS} testid="sc-portfolio-items" />
        <p className="italic opacity-80">
          Together, these contributions create a richer picture of who you
          are than any résumé or social media profile alone.
        </p>
      </Section>

      <Section title="Learning Never Stops" id="learning">
        <p>
          Social Pulse encourages lifelong learning. You might take an online
          course, learn a new language, master photography, explore astronomy,
          identify native plants, publish your first article, or teach a
          practical skill you've developed over decades.
        </p>
        <p><strong>Every new skill becomes another chapter in your lifelong journey of growth.</strong></p>
        <p>Learning becomes visible, celebrated, and shared.</p>
      </Section>

      <Section title="Sharing Knowledge Strengthens Communities" id="knowledge">
        <p>Knowledge grows when it is shared.</p>
        <p>
          Members can answer questions, publish guides, explain complex
          topics, preserve local traditions, mentor newcomers, translate
          educational resources, and contribute expertise across thousands
          of interest communities.
        </p>
        <p>
          Every helpful contribution makes information more accessible and
          strengthens the collective knowledge of society.
        </p>
      </Section>

      <Section title="Volunteering Creates Lasting Impact" id="volunteering">
        <p>Communities thrive when people give their time and talents.</p>
        <p>Social Pulse allows members to document volunteer activities such as:</p>
        <TagList items={VOLUNTEER_ITEMS} testid="sc-volunteer-items" tone="green" />
        <p className="italic opacity-80">
          Verified contributions become part of your lifelong record of
          service, celebrating the difference you've made in your community.
        </p>
      </Section>

      <Section title="Creativity Is a Valuable Contribution" id="creativity">
        <p>
          Art, music, writing, photography, filmmaking, design, craftsmanship,
          and innovation all enrich society.
        </p>
        <p>
          Social Pulse encourages creators to document their work, share their
          creative process, collaborate with others, and inspire future
          generations.
        </p>
        <p>
          Creativity is recognized not only for artistic excellence but also
          for its ability to educate, connect, and improve lives.
        </p>
      </Section>

      {/* ILLUSTRATION 2 — Family Tree */}
      <figure className="my-12" data-testid="sc-illustration-family">
        <img src={ASSET.family}
             alt="Our Family, Our Story — Roots to remember. Bonds that last forever."
             className="w-full rounded-2xl border shadow-sm"
             style={{ borderColor: "var(--wofy-border)" }} />
        <figcaption className="text-center text-xs opacity-60 mt-2 font-mono uppercase tracking-wider">
          Family Tree — Roots to remember. Bonds that last forever.
        </figcaption>
      </figure>

      <Section title="Building Meaningful Connections" id="connections">
        <p>The strongest communities are built on trust and shared purpose.</p>
        <p>
          Social Pulse helps people connect with mentors, educators,
          researchers, professionals, volunteers, artists, entrepreneurs,
          community organizations, and individuals who share similar interests.
        </p>
        <p>
          These relationships can lead to friendships, collaborations,
          research partnerships, volunteer opportunities, career development,
          and lifelong learning.
        </p>
      </Section>

      <Section title="Recognition Through Contribution" id="recognition">
        <p>
          Rather than rewarding attention alone, Social Pulse recognizes
          positive actions that strengthen communities.
        </p>
        <p>Contributions may include:</p>
        <TagList items={RECOGNITION_ITEMS} testid="sc-recognition-items" tone="blue" />
        <p className="italic opacity-80">
          Recognition reflects consistent effort, integrity, expertise,
          and community impact.
        </p>
      </Section>

      <Section title="Supporting Opportunities Throughout Life" id="opportunities">
        <p>
          A trusted record of meaningful contributions can help demonstrate
          initiative, leadership, collaboration, and commitment across many
          stages of life.
        </p>
        <p>Depending on your goals, your Social Pulse profile may strengthen applications for:</p>
        <TagList items={OPPORTUNITY_ITEMS} testid="sc-opportunity-items" tone="amber" />
        <p className="italic opacity-80">
          While each organization makes its own decisions, a well-developed
          profile can provide a richer view of your experiences, interests,
          and contributions beyond traditional credentials.
        </p>
      </Section>

      <Section title="A Legacy That Continues to Grow" id="legacy">
        <p>Social Pulse is more than a social platform.</p>
        <p>
          It is a living record of curiosity, compassion, creativity, and
          service. It tells the story of the places you've explored, the
          people you've helped, the knowledge you've shared, the skills
          you've developed, and the communities you've strengthened.
        </p>
        <p className="font-medium">
          Long after individual posts are forgotten, your contributions
          continue to educate, inspire, and benefit others.
        </p>
      </Section>

      {/* ILLUSTRATION 3 — Celebrate a Life (memorial) */}
      <figure className="my-12" data-testid="sc-illustration-memorial">
        <img src={ASSET.memorial}
             alt="Celebrate a Life — Remember. Honor. Celebrate."
             className="w-full rounded-2xl border shadow-sm"
             style={{ borderColor: "var(--wofy-border)" }} />
        <figcaption className="text-center text-xs opacity-60 mt-2 font-mono uppercase tracking-wider">
          Celebrate a Life — Honoring Memories. Celebrating Lives.
        </figcaption>
      </figure>

      <Section title="Building a Better Digital Society" id="better-society">
        <p>Imagine a world where reputation is earned through kindness instead of conflict.</p>
        <p>Where expertise is valued over misinformation.</p>
        <p>Where volunteering receives as much recognition as entertainment.</p>
        <p>Where learning is celebrated at every age.</p>
        <p>Where communities grow stronger because people choose to contribute rather than simply consume.</p>
        <p className="font-medium mt-2">That is the future Social Pulse seeks to build.</p>

        <div className="mt-6 p-6 rounded-2xl" style={{ background: "linear-gradient(135deg, #ECFDF5, #DBEAFE)" }}>
          <ul className="space-y-1 text-lg">
            <li>Every lesson learned.</li>
            <li>Every skill shared.</li>
            <li>Every question answered.</li>
            <li>Every tree planted.</li>
            <li>Every discovery documented.</li>
            <li>Every act of kindness.</li>
            <li className="font-bold" style={{ color: "#059669" }}>Every contribution matters.</li>
          </ul>
        </div>

        <p className="mt-6">
          Together, we can build a world where <em>Social Capital</em> is
          measured not by popularity, but by knowledge, integrity, service,
          collaboration, and the positive difference each person makes.
        </p>
        <p className="mt-4 text-xl font-display font-medium" style={{ color: "#059669" }}>
          Learn. Create. Explore. Volunteer. Inspire. Connect. Contribute.
        </p>
        <p className="mt-2 italic">
          Build a reputation that reflects not just what you've achieved
          — but the impact you've made on the world.
        </p>
      </Section>

      {/* SOCIAL CAPITAL SOURCES */}
      <section className="mt-16" data-testid="sc-sources">
        <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
          🌍 SOCIAL CAPITAL SOURCES
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0f172a" }}>
          Ten interconnected areas that grow your reputation
        </h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SOURCES.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.title}
                   className="rounded-2xl border p-4 bg-white"
                   style={{ borderColor: "var(--wofy-border)" }}
                   data-testid={`sc-source-${s.title}`}>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                       style={{ background: `${s.color}18`, color: s.color }}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="font-medium text-sm">{s.title}</div>
                </div>
                <div className="mt-2 text-xs opacity-75 leading-relaxed">
                  {s.items.join(" · ")}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-sm opacity-70 italic">And more…</p>
      </section>

      {/* Inline ecosystem widget (matches the homepage) */}
      <SocialCapitalEcosystem />

      {/* Closing manifesto card */}
      <section className="mt-8 rounded-2xl border p-6"
               style={{ borderColor: "var(--wofy-border)", background: "#F0FDF4" }}
               data-testid="sc-closing">
        <p className="text-base sm:text-lg leading-relaxed" style={{ color: "#065f46" }}>
          This broader vision positions <strong>Social Capital</strong> as the
          foundational value system of Social Pulse. Rather than competing with
          platforms that optimize for engagement, it defines a new kind of
          network where reputation grows through verified learning, community
          participation, creativity, and service across an entire lifetime.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/register" className="wofy-btn-primary" data-testid="sc-cta-register">
            <Sparkles className="w-4 h-4" /> Start building your Social Capital
          </Link>
          <Link to="/vision-mission" className="wofy-btn-ghost text-sm" data-testid="sc-cta-about">
            Our vision & mission <ArrowRight className="w-3 h-3" />
          </Link>
          <Link to="/celebrate" className="wofy-btn-ghost text-sm" data-testid="sc-cta-celebrate">
            <Heart className="w-4 h-4" /> Celebrate a life
          </Link>
        </div>
      </section>
    </div>
  );
}

function Section({ title, children, id }) {
  return (
    <section className="mt-12" id={id} data-testid={`sc-section-${id}`}>
      <h2 className="font-display text-2xl sm:text-3xl font-bold mb-3" style={{ color: "#0f172a" }}>
        {title}
      </h2>
      <div className="prose-content space-y-3 text-base sm:text-lg leading-relaxed"
           style={{ color: "#1f2937" }}>
        {children}
      </div>
    </section>
  );
}

function TagList({ items, testid, tone = "emerald" }) {
  const style = {
    emerald: { color: "#065f46", background: "#d1fae5", border: "#a7f3d0" },
    green:   { color: "#166534", background: "#dcfce7", border: "#bbf7d0" },
    blue:    { color: "#075985", background: "#e0f2fe", border: "#bae6fd" },
    amber:   { color: "#92400e", background: "#fef3c7", border: "#fde68a" },
  }[tone] || { color: "#065f46", background: "#d1fae5", border: "#a7f3d0" };
  return (
    <div className="flex flex-wrap gap-1.5 my-3" data-testid={testid}>
      {items.map((it) => (
        <span key={it}
              className="inline-block text-xs px-2.5 py-1 rounded-full border"
              style={{ color: style.color, background: style.background, borderColor: style.border }}>
          {it}
        </span>
      ))}
    </div>
  );
}
